import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '../api';
import { useMyTenants, date } from '../hooks';
import { Modal } from '../components/Modal';
import { Icon } from '../shared/Icon';
import type { Tenant } from '../types';

/** Выбор тенанта — первый экран. Здесь же администратор платформы заводит новых операторов. */
export function TenantsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useMyTenants();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', invoiceNumberPrefix: 'INV', billingDay: 1 });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'МедКабинет Биллинг';
  }, []);

  const create = useMutation({
    mutationFn: () => api.post<Tenant>('/tenants', form),
    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: ['my-tenants'] });
      setCreating(false);
      navigate(`/t/${tenant.id}/dashboard`);
    },
    onError: (err) => setError((err as ApiError).message),
  });

  if (me.isLoading) return <div className="state">Загрузка…</div>;
  if (me.isError) {
    return (
      <div className="state">
        {(me.error as ApiError).status === 401 ? 'Переходим ко входу…' : 'Сервис биллинга недоступен'}
      </div>
    );
  }

  const tenants = me.data ?? [];
  const canCreate = tenants.some((t) => t.platformAdmin) || tenants.length === 0;

  return (
    <div className="entry-page">
      <div className="entry-wrap">
        <div className="entry-head">
          <div className="entry-logo">
            <div className="entry-logo-icon">
              <Icon name="activity" size={18} />
            </div>
            <div className="entry-logo-name">МедКабинет Биллинг</div>
          </div>
          <h1>Выберите тенанта</h1>
          <p>Тенант — оператор биллинга: свои абоненты, тарифы и деньги.</p>
        </div>

        {tenants.length === 0 ? (
          <div className="card">
            <div className="state-title">Доступных тенантов нет</div>
            <p className="muted">
              Попросите владельца добавить вас — или создайте свой, если вы администратор платформы.
            </p>
            {canCreate && (
              <div className="state-actions">
                <button className="btn btn-primary" onClick={() => setCreating(true)}>
                  <Icon name="plus" />
                  Создать тенанта
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="tenant-grid">
              {tenants.map(({ tenant, role, platformAdmin }) => (
                <button
                  key={tenant.id}
                  className="tenant-card"
                  onClick={() => navigate(`/t/${tenant.id}/dashboard`)}
                >
                  <div className="tenant-name">{tenant.name}</div>
                  <div className="muted small">
                    <code>{tenant.code}</code>
                  </div>
                  <div className="tenant-meta">
                    <span className="badge badge-gray">
                      {ROLE_LABELS[role ?? ''] ?? (platformAdmin ? 'платформа' : '')}
                    </span>
                    {tenant.status === 'SUSPENDED' && (
                      <span className="badge badge-amber">приостановлен</span>
                    )}
                    <span className="muted small">с {date(tenant.created)}</span>
                  </div>
                </button>
              ))}
            </div>

            {canCreate && (
              <div className="state-actions">
                <button className="btn btn-secondary" onClick={() => setCreating(true)}>
                  <Icon name="plus" />
                  Новый тенант
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {creating && (
        <Modal title="Новый тенант" onClose={() => setCreating(false)}>
          <div className="form-group">
            <label className="form-label" htmlFor="tenant-code">
              Код
            </label>
            <input
              id="tenant-code"
              className="input"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="acme-telecom"
              autoFocus
            />
            <div className="form-hint">
              Латиница, цифры и дефис. Виден в отчётах, потом его не изменить.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="tenant-name">
              Название
            </label>
            <input
              id="tenant-name"
              className="input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="ACME Telecom"
            />
          </div>

          <div className="row">
            <div className="form-group">
              <label className="form-label" htmlFor="tenant-prefix">
                Префикс счетов
              </label>
              <input
                id="tenant-prefix"
                className="input"
                value={form.invoiceNumberPrefix}
                onChange={(e) => setForm({ ...form, invoiceNumberPrefix: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="tenant-day">
                День выставления
              </label>
              <input
                id="tenant-day"
                className="input"
                type="number"
                min={1}
                max={28}
                value={form.billingDay}
                onChange={(e) => setForm({ ...form, billingDay: Number(e.target.value) })}
              />
            </div>
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setCreating(false)}>
              Отмена
            </button>
            <button
              className="btn btn-primary"
              disabled={!form.code || !form.name || create.isPending}
              onClick={() => {
                setError(null);
                create.mutate();
              }}
            >
              Создать
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'владелец',
  ADMIN: 'администратор',
  VIEWER: 'наблюдатель',
};
